export type QuestionOption = Record<string, string>;

export interface Question {
  id: string;
  question: string;
  options: QuestionOption;
  multiSelect?: boolean;
}

export interface LogicCondition {
  conditions: Record<string, string | string[] | number>;
  tier: string;
  range: [number, number];
  hourlyRate: string;
  estimatedHours: string;
}

export interface ProjectCategory {
  questions: Question[];
  logic: LogicCondition[];
}

export interface EstimatorLogic {
  projectTypes: string[];
  [key: string]: string[] | ProjectCategory;
}

export const estimatorData: EstimatorLogic = {
  projectTypes: ['website', 'mobile_app', 'ai_agent'],

  website: {
    questions: [
      {
        id: 'pages',
        question: 'How many pages do you need?',
        options: {
          '1_5': '1–5 pages',
          '6_12': '6–12 pages',
          '12_plus': '12+ pages',
        },
      },
      {
        id: 'goal',
        question: 'What is the primary goal of the website?',
        options: {
          presence: 'Online presence',
          conversion: 'Lead generation / conversions',
          custom_system: 'Custom systems or backend',
        },
      },
      {
        id: 'features',
        question: 'Do you need any advanced features?',
        multiSelect: true,
        options: {
          none: 'None (No advanced features)',
          backend: 'Custom backend',
          database: 'Database',
          api: 'API integrations',
          ecommerce: 'E-commerce',
          ai: 'AI features',
          multilang: 'Multi-language',
          animations: 'Advanced animations',
        },
      },
    ],

    logic: [
      {
        conditions: {
          pages: '1_5',
        },
        tier: 'Starter Plan',
        range: [1500, 3750],
        hourlyRate: '$75 / hr',
        estimatedHours: '20-50',
      },
      {
        conditions: {
          pages: '1_5',
          features: ['none'],
        },
        tier: 'Starter Plan',
        range: [1500, 3750],
        hourlyRate: '$75 / hr',
        estimatedHours: '20-50',
      },
      {
        conditions: {
          pages: '6_12',
        },
        tier: 'Conversion Plan',
        range: [4500, 6750],
        hourlyRate: '$90 / hr',
        estimatedHours: '50-75',
      },
      {
        conditions: {
          goal: 'conversion',
        },
        tier: 'Conversion Plan',
        range: [4500, 6750],
        hourlyRate: '$90 / hr',
        estimatedHours: '50-75',
      },
      {
        conditions: {
          features: ['ecommerce', 'ai'],
        },
        tier: 'Conversion Plan',
        range: [4500, 6750],
        hourlyRate: '$90 / hr',
        estimatedHours: '50-75',
      },
      {
        conditions: {
          pages: '12_plus',
        },
        tier: 'Custom Plan',
        range: [12000, 12000],
        hourlyRate: '$150 / hr',
        estimatedHours: '80+',
      },
      {
        conditions: {
          features: ['backend', 'database', 'api'],
        },
        tier: 'Custom Plan',
        range: [12000, 12000],
        hourlyRate: '$150 / hr',
        estimatedHours: '80+',
      },
    ],
  },

  mobile_app: {
    questions: [
      {
        id: 'screens',
        question: 'How many screens will your app have?',
        options: {
          '3_6': '3–6 screens',
          '7_15': '7–15 screens',
          '15_plus': '15+ screens',
        },
      },
      {
        id: 'features',
        question: 'Which features & integrations do you need?',
        multiSelect: true,
        options: {
          auth: 'Login / Authentication',
          backend: 'Backend system',
          realtime: 'Real-time features (chat, live updates, sockets)',
          payment: 'Payment integration',
          maps: 'Maps integration',
          ai: 'AI features',
          third_party: 'Third-party APIs',
        },
      },
    ],

    logic: [
      {
        conditions: {
          screens: '3_6',
          featuresCountMax: 1,
        },
        tier: 'Mobile Launch',
        range: [3100, 4950],
        hourlyRate: '$90 / hr',
        estimatedHours: '35-55',
      },
      {
        conditions: {
          screens: '7_15',
        },
        tier: 'Mobile Growth',
        range: [6600, 11000],
        hourlyRate: '$110 / hr',
        estimatedHours: '60-100',
      },
      {
        conditions: {
          featuresCountMin: 2,
          featuresCountMax: 4,
        },
        tier: 'Mobile Growth',
        range: [6600, 11000],
        hourlyRate: '$110 / hr',
        estimatedHours: '60-100',
      },
      {
        conditions: {
          screens: '15_plus',
        },
        tier: 'Mobile Enterprise',
        range: [40000, 40000],
        hourlyRate: '$160 / hr',
        estimatedHours: '250+',
      },
      {
        conditions: {
          featuresCountMin: 5,
        },
        tier: 'Mobile Enterprise',
        range: [40000, 40000],
        hourlyRate: '$160 / hr',
        estimatedHours: '250+',
      },
    ],
  },

  ai_agent: {
    questions: [
      {
        id: 'agent_type',
        question: 'What type of AI agent do you need?',
        options: {
          simple: 'Simple automation',
          workflow: 'Multi-step workflow',
          custom_logic: 'Fully custom logic',
          multi_platform: 'Multi-platform integration',
        },
      },
      {
        id: 'integrations',
        question: 'How many platforms does it need to connect to?',
        options: {
          '1_2': '1–2 platforms',
          '3_plus': '3+ platforms',
        },
      },
      {
        id: 'analytics',
        question: 'Do you need analytics or dashboards?',
        options: {
          yes: 'Yes',
          no: 'No',
        },
      },
      {
        id: 'advanced_reasoning',
        question: 'Does the agent need memory, reasoning, or custom logic?',
        options: {
          yes: 'Yes',
          no: 'No',
        },
      },
    ],

    logic: [
      {
        conditions: {},
        tier: 'Starter Agents',
        range: [1000, 2000],
        hourlyRate: '$100 / hr',
        estimatedHours: '10-20',
      },
      {
        conditions: {
          integrations: '3_plus',
        },
        tier: 'Advanced Agents',
        range: [3125, 5000],
        hourlyRate: '$125 / hr',
        estimatedHours: '25-40',
      },
      {
        conditions: {
          analytics: 'yes',
        },
        tier: 'Advanced Agents',
        range: [3125, 5000],
        hourlyRate: '$125 / hr',
        estimatedHours: '25-40',
      },
      {
        conditions: {
          agent_type: 'custom_logic',
        },
        tier: 'Enterprise Agents',
        range: [10500, 10500],
        hourlyRate: '$150 / hr',
        estimatedHours: '70+',
      },
      {
        conditions: {
          advanced_reasoning: 'yes',
        },
        tier: 'Enterprise Agents',
        range: [10500, 10500],
        hourlyRate: '$150 / hr',
        estimatedHours: '70+',
      },
    ],
  },
};
